/**
 * Frontend mirror of backend's `Options/CustomerOptions.DefaultsFor`.
 * VITE_CUSTOMER (a build-time choice - Stroomkaart and Bristol get separate
 * Docker images) selects which entry the app uses; vite.config.ts further
 * exports the logo paths and title as %VITE_BRAND_*% placeholders that get
 * substituted into every HTML at build time.
 */

export type Customer = 'Stroomkaart' | 'Bristol';

export interface BrandConfig {
  /** Display name. Substituted into every HTML's <title> via %VITE_BRAND_TITLE% at build time. */
  title: string;
  /** Public-site light-bg logo. Substituted into <img data-brand-logo> via %VITE_BRAND_LOGO%. */
  logoSrc: string;
  /** Admin top-bar dark-bg logo. Substituted into <img data-brand-logo="white"> via %VITE_BRAND_LOGO_WHITE%. */
  logoWhiteSrc: string;
  /**
   * Optional secondary brand logo shown next to the primary logo in the
   * public nav (co-branding / partner mark). Undefined = no secondary mark.
   * Bristol uses this for the Weston-super-Mare council partnership badge.
   */
  secondaryLogoSrc?: string;
  /** Initial map centre as [lng, lat]. */
  mapCenter: [number, number];
  /** Initial map zoom. */
  mapZoom: number;
  /** Currency symbol shown alongside any tariff string the backend hasn't formatted. */
  currencySymbol: string;
  /** Comma-separated ISO country codes for the Nominatim search filter. */
  searchCountryCodes: string;
  /** Supported UI languages for this customer. Switcher hides itself when length === 1. */
  languages: readonly string[];
  /** Initial language when the user has no URL/localStorage override. */
  defaultLanguage: string;
  /**
   * Per-customer feature gates. Anything that *permanently* differs between
   * deployments lives here - runtime-tweakable toggles still belong in the
   * AppSettings table. Mirrored by <c>CustomerOptions.Features</c> on the
   * backend; add a flag on both sides when you introduce one.
   */
  features: BrandFeatures;
}

export interface BrandFeatures {
  /** Show charging stations on the public map (filter chip + marker layer). */
  publicMapChargingStations: boolean;
  /** Show water points on the public map. */
  publicMapWaterPoints: boolean;
  /** Enable the Energie-scan admin module (nav entry + API endpoints). */
  energieScanWorkspace: boolean;
}

export const BRANDS: Record<Customer, BrandConfig> = {
  Stroomkaart: {
    title: 'Stroomkaart',
    logoSrc: '/assets/brand/stroomkaart-logo.svg',
    logoWhiteSrc: '/assets/brand/stroomkaart-logo-white.svg',
    mapCenter: [5.291266, 52.132633],
    mapZoom: 7,
    currencySymbol: '€',
    searchCountryCodes: 'nl,be,lu',
    languages: ['nl', 'en'],
    defaultLanguage: 'nl',
    features: {
      publicMapChargingStations: true,
      publicMapWaterPoints: true,
      energieScanWorkspace: true,
    },
  },
  Bristol: {
    title: 'Bristol EV Map',
    logoSrc: '/assets/brand/bristol-logo.png',
    logoWhiteSrc: '/assets/brand/bristol-logo-white.png',
    secondaryLogoSrc: '/assets/brand/bristol-secondary-logo.svg',
    mapCenter: [-2.5879, 51.4545],
    mapZoom: 12,
    currencySymbol: '£',
    searchCountryCodes: 'gb',
    languages: ['en'],
    defaultLanguage: 'en',
    features: {
      publicMapChargingStations: false,
      publicMapWaterPoints: false,
      energieScanWorkspace: false,
    },
  },
};

export const DEFAULT_CUSTOMER: Customer = 'Stroomkaart';

export function isCustomer(value: unknown): value is Customer {
  return typeof value === 'string' && value in BRANDS;
}
